import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { useReports } from '../../hooks/useReports';
import { useProjectConnections } from '../../hooks/useProjects';
import { 
  FileText, 
  Search, 
  TrendingUp, 
  Users, 
  ShoppingCart, 
  BarChart3, 
  PieChart, 
  Target,
  Calendar,
  Globe,
  Mail,
  Smartphone,
  Star,
  CheckCircle,
  Loader,
  Sparkles,
  Eye,
  Filter,
  AlertCircle,
  Database,
  Link,
  Zap
} from 'lucide-react';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  widgets: string[];
  estimatedTime: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  popular: boolean;
  icon: React.ComponentType<any> | string;
  preview: string;
  requiredConnections: string[];
  optionalConnections: string[];
  comingSoon?: boolean;
  comingSoonReason?: string;
}

interface ReportTemplatesProps {
  onSelectTemplate: (template: ReportTemplate) => void;
  projectId?: string;
}

export function ReportTemplates({ onSelectTemplate, projectId }: ReportTemplatesProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDataSource, setSelectedDataSource] = useState('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);

  const { useTemplates } = useReports();
  const { data: templatesData, isLoading, error } = useTemplates();
  
  // Get project connections if projectId is provided
  const { data: connectionsData } = useProjectConnections(projectId || '');
  const availableDataSources = connectionsData?.availableDataSources || [];

  // Data source display names and icons - Updated to use google_analytics instead of ga4
  const dataSourceInfo = {
    google_analytics: { name: 'Google Analytics 4', icon: BarChart3, color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' },
    ga4: { name: 'Google Analytics 4', icon: BarChart3, color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' }, // Keep for backward compatibility
    search_console: { name: 'Search Console', icon: Search, color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30' },
    google_ads: { name: 'Google Ads', icon: Target, color: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30' },
    facebook_ads: { name: 'Facebook Ads', icon: Users, color: 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30' },
    shopify: { name: 'Shopify', icon: ShoppingCart, color: 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30' },
    hubspot: { name: 'HubSpot', icon: Mail, color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30' }
  };

  // Default templates as fallback - Updated to use google_analytics instead of ga4
  const defaultTemplates: ReportTemplate[] = [
    {
      id: 'marketing-overview',
      name: 'Marketing Overview',
      description: 'Comprehensive overview of all marketing channels and performance metrics',
      category: 'Marketing',
      widgets: ['Traffic Sources', 'Conversion Rates', 'Campaign Performance', 'ROI Analysis'],
      estimatedTime: '5 minutes',
      difficulty: 'Beginner',
      popular: true,
      icon: TrendingUp,
      preview: 'Get insights into your overall marketing performance with key metrics and trends.',
      requiredConnections: ['google_analytics'],
      optionalConnections: ['google_ads']
    },
    {
      id: 'traffic-analysis',
      name: 'Website Traffic Analysis',
      description: 'Deep dive into website traffic patterns, sources, and user behavior',
      category: 'Analytics',
      widgets: ['Page Views', 'Unique Visitors', 'Bounce Rate', 'Session Duration'],
      estimatedTime: '3 minutes',
      difficulty: 'Beginner',
      popular: true,
      icon: Globe,
      preview: 'Understand your website traffic patterns and identify optimization opportunities.',
      requiredConnections: ['google_analytics'],
      optionalConnections: []
    },
    {
      id: 'conversion-funnel',
      name: 'Conversion Funnel Report',
      description: 'Track user journey from awareness to conversion with detailed funnel analysis',
      category: 'Conversion',
      widgets: ['Funnel Stages', 'Drop-off Points', 'Conversion Rates', 'Revenue Attribution'],
      estimatedTime: '7 minutes',
      difficulty: 'Intermediate',
      popular: false,
      icon: Target,
      preview: 'Optimize your conversion funnel by identifying bottlenecks and opportunities.',
      requiredConnections: ['google_analytics'],
      optionalConnections: ['google_ads']
    },
    {
      id: 'seo-performance',
      name: 'SEO Performance Report',
      description: 'Monitor search engine rankings, organic traffic, and keyword performance',
      category: 'SEO',
      widgets: ['Organic Traffic', 'Keyword Rankings', 'Click-through Rates', 'Search Impressions'],
      estimatedTime: '6 minutes',
      difficulty: 'Intermediate',
      popular: true,
      icon: Search,
      preview: 'Boost your search engine visibility with comprehensive SEO insights.',
      requiredConnections: ['search_console'],
      optionalConnections: ['google_analytics']
    }
  ];

  // Use API data if available, otherwise use default templates
  const templates: ReportTemplate[] = useMemo(() => {
    if (templatesData?.templates) {
      return templatesData.templates.map((t: any) => ({
        ...t,
        requiredConnections: t.requiredConnections || [],
        optionalConnections: t.optionalConnections || []
      }));
    }
    return defaultTemplates;
  }, [templatesData]);

  // Get unique categories and data sources
  const categories = ['all', ...Array.from(new Set(templates.map((t: ReportTemplate) => t.category)))];
  const allDataSources = Array.from(new Set([
    ...templates.flatMap(t => [...t.requiredConnections, ...t.optionalConnections])
  ]));
  const dataSourceFilters = ['all', ...allDataSources];

  // Check if template can be used (has required connections and not coming soon)
  const canUseTemplate = (template: ReportTemplate) => {
    // Block coming soon templates
    if (template.comingSoon) return false;
    
    if (!projectId || !template.requiredConnections.length) return true;
    return template.requiredConnections.every(conn => availableDataSources.includes(conn));
  };

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return templates.filter((template: ReportTemplate) => {
      const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           template.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
      const matchesDataSource = selectedDataSource === 'all' || 
                               template.requiredConnections.includes(selectedDataSource) ||
                               template.optionalConnections.includes(selectedDataSource);
      const matchesAvailability = !showOnlyAvailable || canUseTemplate(template);
      
      return matchesSearch && matchesCategory && matchesDataSource && matchesAvailability;
    });
  }, [templates, searchQuery, selectedCategory, selectedDataSource, showOnlyAvailable, availableDataSources, projectId]);

  const handleTemplateClick = (template: ReportTemplate) => {
    if (!canUseTemplate(template)) {
      return; // Block selection if required connections are missing
    }
    
    setSelectedTemplateId(template.id);
    // Small delay to show the loading state
    setTimeout(() => {
      onSelectTemplate(template);
      setSelectedTemplateId(null);
    }, 300);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
      case 'Advanced': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getIconComponent = (iconName: string | React.ComponentType<any>) => {
    // If it's already a React component, return it
    if (typeof iconName === 'function') {
      return iconName;
    }
    
    // Map string icon names to React components
    const iconMap: Record<string, React.ComponentType<any>> = {
      'TrendingUp': TrendingUp,
      'Globe': Globe,
      'Target': Target,
      'Search': Search,
      'Users': Users,
      'ShoppingCart': ShoppingCart,
      'Mail': Mail,
      'Smartphone': Smartphone,
      'BarChart3': BarChart3,
      'FileText': FileText
    };
    return iconMap[iconName] || FileText;
  };

  const renderDataSourceBadges = (template: ReportTemplate) => {
    const allConnections = [...template.requiredConnections, ...template.optionalConnections];
    const uniqueConnections = Array.from(new Set(allConnections));
    
    return uniqueConnections.slice(0, 3).map((conn) => {
      const info = dataSourceInfo[conn as keyof typeof dataSourceInfo];
      const IconComponent = info?.icon || Database;
      const isRequired = template.requiredConnections.includes(conn);
      const isConnected = availableDataSources.includes(conn);
      
      return (
        <div
          key={conn}
          className={`inline-flex items-center px-2 py-1 rounded text-xs ${
            isConnected 
              ? info?.color || 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
              : 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
          }`}
        >
          <IconComponent className="w-3 h-3 mr-1" />
          {info?.name || conn.toUpperCase()}
          {isRequired && !isConnected && <AlertCircle className="w-3 h-3 ml-1" />}
        </div>
      );
    });
  };

  if (isLoading) {
    return (
      <Card className="p-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600 dark:text-blue-400" />
          <p className="text-gray-600 dark:text-gray-300">Loading templates...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Failed to load templates</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Using default templates instead</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Report Templates</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Start with pre-built templates for common use cases
          </p>
        </div>
        
                 {/* Available Data Sources */}
         {availableDataSources.length > 0 && (
           <div className="flex items-center space-x-2">
             <span className="text-sm text-gray-600 dark:text-gray-400">Connected:</span>
             {availableDataSources.slice(0, 3).map((source: string) => {
               const info = dataSourceInfo[source as keyof typeof dataSourceInfo];
               const IconComponent = info?.icon || Database;
               return (
                 <div key={source} className={`px-2 py-1 rounded text-xs ${info?.color || 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'}`}>
                   <IconComponent className="w-3 h-3 inline mr-1" />
                   {info?.name || source.toUpperCase()}
                 </div>
               );
             })}
             {availableDataSources.length > 3 && (
               <span className="text-xs text-gray-500 dark:text-gray-400">+{availableDataSources.length - 3} more</span>
             )}
           </div>
         )}
      </div>

      {/* Filters */}
      <Card className="p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="min-w-[180px]">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>

          {/* Data Source Filter */}
          <div className="min-w-[180px]">
            <select
              value={selectedDataSource}
              onChange={(e) => setSelectedDataSource(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {dataSourceFilters.map((dataSource) => (
                <option key={dataSource} value={dataSource}>
                  {dataSource === 'all' ? 'All Data Sources' : (dataSourceInfo[dataSource as keyof typeof dataSourceInfo]?.name || dataSource.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          {/* Show Only Available Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-available"
              checked={showOnlyAvailable}
              onChange={(e) => setShowOnlyAvailable(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
            />
            <label htmlFor="show-available" className="text-sm text-gray-700 dark:text-gray-300">
              Available only
            </label>
          </div>
        </div>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => {
          const IconComponent = getIconComponent(template.icon);
          const canUse = canUseTemplate(template);
          const isLoading = selectedTemplateId === template.id;
          const isComingSoon = template.comingSoon;
          
          return (
            <Card
              key={template.id}
              className={`relative p-6 transition-all duration-200 cursor-pointer ${
                canUse && !isComingSoon
                  ? 'hover:shadow-lg hover:scale-105 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
                  : 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
              }`}
              onClick={() => canUse && !isComingSoon && handleTemplateClick(template)}
            >
              {/* Loading Overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90 flex items-center justify-center rounded-lg z-10">
                  <div className="flex items-center space-x-2">
                    <Loader className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-blue-600 dark:text-blue-400">Loading...</span>
                  </div>
                </div>
              )}

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  canUse && !isComingSoon
                    ? 'bg-gradient-to-r from-blue-100 dark:from-blue-900/30 to-purple-100 dark:to-purple-900/30' 
                    : 'bg-gray-200 dark:bg-gray-600'
                }`}>
                  <IconComponent className={`w-6 h-6 ${
                    canUse && !isComingSoon ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                  }`} />
                </div>
                
                <div className="flex items-center space-x-1">
                  {template.popular && !isComingSoon && (
                    <Badge className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-0">
                      <Star className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                  {isComingSoon && (
                    <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-0">
                      Coming Soon
                    </Badge>
                  )}
                  <Badge className={`border-0 ${getDifficultyColor(template.difficulty)}`}>
                    {template.difficulty}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="mb-4">
                <h3 className={`text-lg font-semibold mb-2 ${
                  canUse && !isComingSoon ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {template.name}
                </h3>
                <p className={`text-sm leading-relaxed ${
                  canUse && !isComingSoon ? 'text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {template.description}
                </p>
              </div>

              {/* Widgets Preview */}
              <div className="mb-4">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Included widgets:</div>
                <div className="flex flex-wrap gap-1">
                  {template.widgets.slice(0, 3).map((widget, index) => (
                    <span 
                      key={index}
                      className={`text-xs px-2 py-1 rounded ${
                        canUse && !isComingSoon
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {widget}
                    </span>
                  ))}
                  {template.widgets.length > 3 && (
                    <span className={`text-xs px-2 py-1 rounded ${
                      canUse && !isComingSoon
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                    }`}>
                      +{template.widgets.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Data Sources */}
              {(template.requiredConnections.length > 0 || template.optionalConnections.length > 0) && (
                <div className="mb-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Data sources:</div>
                  <div className="flex flex-wrap gap-1">
                    {renderDataSourceBadges(template)}
                  </div>
                </div>
              )}

              {/* Coming Soon Reason */}
              {isComingSoon && template.comingSoonReason && (
                <div className="mb-4 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded">
                  <p className="text-xs text-orange-700 dark:text-orange-300">{template.comingSoonReason}</p>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <Calendar className="w-3 h-3 mr-1" />
                  {template.estimatedTime}
                </div>
                
                {canUse && !isComingSoon ? (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white">
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                ) : (
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    {isComingSoon ? (
                      <>
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Coming Soon
                      </>
                    ) : (
                      <>
                        <Link className="w-3 h-3 mr-1" />
                        Connect Required
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* No Results */}
      {filteredTemplates.length === 0 && (
        <Card className="p-12 text-center bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No templates found</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Try adjusting your filters or search terms
          </p>
          <Button 
            variant="outline" 
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setSelectedDataSource('all');
              setShowOnlyAvailable(false);
            }}
            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Clear Filters
          </Button>
        </Card>
      )}
    </div>
  );
} 